import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { PHONE, TEL, scrollToId, EASE } from "./data";

const LINES = [
  { text: "Your lot.", cls: "" },
  { text: "Your rules.", cls: "text-outline" },
  { text: "We enforce.", cls: "text-[var(--signal)]" },
];

const Line = ({ children, i, cls }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.35 + i * 0.14 }}
    >
      {children}
    </motion.span>
  </span>
);

const useTilt = () => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 20 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${sx} ${sy}, rgba(140,200,70,0.26), transparent 60%)`;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };
  return { rx, ry, spot, onMove, onLeave };
};

const HeroImage = () => {
  const { rx, ry, spot, onMove, onLeave } = useTilt();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
      className="relative [perspective:1200px]"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-image-frame"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="clip-notch relative aspect-[4/5] w-full overflow-hidden bg-[var(--ink-2)] sm:aspect-[5/4] lg:aspect-[4/5]">
          <img src="/img/hero-tow.jpg" alt="Warrior tow truck on patrol in a Charlotte parking lot at night" className="h-full w-full scale-110 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-transparent to-transparent" />
          <motion.div className="absolute inset-0 mix-blend-screen" style={{ background: spot }} />
        </div>
        <div className="absolute -bottom-5 -left-3 flex items-center gap-3 bg-[var(--bone)] px-4 py-3 text-[var(--ink)] [transform:translateZ(60px)] sm:-left-6">
          <span className="pulse-dot h-2.5 w-2.5 rounded-full bg-[var(--signal)]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Charlotte, NC · On patrol</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-[110px] lg:min-h-screen lg:pt-[130px]">
      <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-[var(--signal)] opacity-[0.08] blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7 lg:pt-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400"
          >
            <span className="h-px w-10 bg-[var(--signal)]" /> Private Property Impound · Charlotte
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display text-[16vw] font-black uppercase leading-[0.86] tracking-tight sm:text-[12vw] lg:text-[8.6vw] 2xl:text-[150px]">
            {LINES.map((l, i) => <Line key={l.text} i={i} cls={l.cls}>{l.text}</Line>)}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
            className="mt-10 max-w-xl"
          >
            <p className="text-base leading-relaxed text-zinc-300 sm:text-lg">
              Warrior Towing &amp; Recovery keeps Charlotte parking lots clear for the people who belong there —
              apartment communities, HOAs, retail centers and businesses. Unauthorized vehicles, handled.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={TEL} data-testid="hero-phone-cta" className="group flex min-h-[52px] items-center gap-3 bg-[var(--signal)] px-6 font-display text-xl font-extrabold uppercase tracking-wide text-[var(--ink)] transition-transform duration-300 hover:-translate-y-1">
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" strokeWidth={2.5} /> {PHONE}
              </a>
              <button onClick={() => scrollToId("retrieval")} data-testid="hero-towed-btn" className="flex min-h-[52px] items-center gap-2 border border-[var(--line)] px-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-200 transition-colors duration-300 hover:border-[var(--signal)] hover:text-white">
                My car was towed <ArrowDown className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5">
          <HeroImage />
        </motion.div>
      </div>
    </section>
  );
};
